import { BUILDING_TYPE } from '~/lib/graphql/types';
import type { QuoteInput, Utm } from '~/lib/store/types';

import { posthog } from './posthog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrackingPayload {
    quote: QuoteInput;
    set: 'desktop' | 'mobile';
    direction: 'back' | 'continue';
    utm: Utm;
}

// ---------------------------------------------------------------------------
// Building type labels (used for enriching analytics events)
// ---------------------------------------------------------------------------

const buildingTypeLabels: Record<
    BUILDING_TYPE,
    { value: number; label: string }
> = {
    [BUILDING_TYPE.LANDED_HOUSE]: { value: 1, label: 'Landed house' },
    [BUILDING_TYPE.CONDOMINIUM]: { value: 2, label: 'Condominium' },
    [BUILDING_TYPE.HDB_FLAT]: { value: 3, label: 'HDB flat' },
    [BUILDING_TYPE.RENTED_COMMERCIAL_PROPERTY]: {
        value: 4,
        label: 'Rented commercial property'
    },
    [BUILDING_TYPE.OWNED_COMMERCIAL_PROPERTY]: {
        value: 5,
        label: 'Owned commercial property'
    }
};

// ---------------------------------------------------------------------------
// GTM dataLayer push
// ---------------------------------------------------------------------------

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[];
    }
}

function pushEvent(data: Record<string, unknown>): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(data);
    }
}

// ---------------------------------------------------------------------------
// Property type helper
// ---------------------------------------------------------------------------

const RESIDENTIAL_TYPES: BUILDING_TYPE[] = [
    BUILDING_TYPE.LANDED_HOUSE,
    BUILDING_TYPE.CONDOMINIUM,
    BUILDING_TYPE.HDB_FLAT
];

function getPropertyType(
    type: BUILDING_TYPE | null
): 'residential' | 'commercial' {
    return type && RESIDENTIAL_TYPES.includes(type)
        ? 'residential'
        : 'commercial';
}

// ---------------------------------------------------------------------------
// GA events (matching old calculator's exact dataLayer shape)
// ---------------------------------------------------------------------------

export function trackSelectedAddress(type: BUILDING_TYPE | null): void {
    pushEvent({
        event: 'simulator_selected_address',
        property_type: getPropertyType(type)
    });
}

export function trackAnsweredQuestions(type: BUILDING_TYPE | null): void {
    pushEvent({
        event: 'simulator_answered_questions',
        property_type: getPropertyType(type)
    });
}

export function trackSelectedArea(type: BUILDING_TYPE | null): void {
    pushEvent({
        event: 'simulator_selected_area',
        property_type: getPropertyType(type)
    });
}

export function trackSubmitSimulation(params: {
    leadId: string;
    email: string;
    phone: string;
    type: BUILDING_TYPE | null;
}): void {
    pushEvent({
        event: 'submit_simulation',
        lead_id: params.leadId,
        user_email: params.email,
        user_phone_number: params.phone,
        property_type: getPropertyType(params.type)
    });
}

export function trackSelectOffer(params: {
    type: BUILDING_TYPE | null;
    code: string;
    option: string;
}): void {
    pushEvent({
        event: 'select_offer',
        property_type: getPropertyType(params.type),
        eventModel: {
            event_category: 'user_select_offer',
            event_label: `${params.code}_${params.option.toLowerCase()}`
        }
    });
}

// ---------------------------------------------------------------------------
// Enriched event (generic — used for detailed tracking)
// ---------------------------------------------------------------------------

/**
 * Push an event to GTM dataLayer and PostHog.
 */
export function trackEvent(eventName: string, payload: TrackingPayload): void {
    if (typeof window === 'undefined') return;

    const { quote, set, direction, utm } = payload;

    const addressComponents = quote.address_components?.reduce(
        (result, component) => {
            result['address_components_' + component.types[0]] =
                component.long_name;
            return result;
        },
        {} as Record<string, string>
    );

    const buildingInfo = quote.type ? buildingTypeLabels[quote.type] : null;

    const props = {
        ...quote,
        type: buildingInfo,
        type_label: buildingInfo?.label ?? null,
        type_value: buildingInfo?.value ?? null,
        set,
        direction,
        utm,
        contact_email: quote.contact?.email,
        contact_name: quote.contact?.name,
        contact_company: quote.contact?.company,
        contact_job_title: quote.contact?.job_title,
        contact_phone: quote.contact?.phone,
        ...addressComponents
    };

    // GTM
    if (window.dataLayer) {
        window.dataLayer.push({ event: eventName, ...props });
    }

    // PostHog
    posthog.capture(eventName, props);
}
